import React, { useState, useEffect } from 'react';
import { useConfig } from '../../context/ConfigContext';
import { C, S } from '../../utils/styles';
import { useToast } from '../Toast';
import Icon from '../Icon';
import HelpSection from './HelpSection';

const EVENTS = [
  {
    key: 'contract_signed',
    label: 'Contract Signed',
    description: 'Auto-message sent when an employer contact signs a contract.',
    variables: ['contactName', 'contractName', 'companyName'],
  },
  {
    key: 'banking_verified',
    label: 'Banking Verified',
    description: 'Auto-message sent when the employer completes banking setup (Plaid or manual ACH).',
    variables: ['bankName', 'accountMasked'],
  },
  {
    key: 'census_uploaded',
    label: 'Census Uploaded',
    description: 'Auto-message sent when the employer uploads their employee census.',
    variables: ['count', 'avgSalary'],
  },
  {
    key: 'enrollment_milestone',
    label: 'Enrollment Milestone',
    description: 'Auto-message sent when enrolled count hits a milestone (5, 10, 25, 50, 75, 100, then every 25).',
    variables: ['count'],
  },
];

const DEFAULTS = {
  contract_signed: {
    enabled: true,
    template: "✓ {contactName} signed {contractName}. That's one step closer to go-live!",
  },
  banking_verified: {
    enabled: true,
    template: "✓ Banking verified ({bankName} {accountMasked}). You're all set for payroll integration.",
  },
  census_uploaded: {
    enabled: true,
    template: "✓ Census uploaded with {count} employees (avg salary {avgSalary}). Your full workforce analysis is ready to review.",
  },
  enrollment_milestone: {
    enabled: true,
    template: "🎉 {count} employees have enrolled! Your program is off to a strong start.",
  },
};

export default function AutoMessagesEditor() {
  const { config, updateConfig } = useConfig();
  const showToast = useToast();
  const [templates, setTemplates] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setTemplates(JSON.parse(JSON.stringify(config?.autoMessageTemplates || DEFAULTS)));
  }, [config?.autoMessageTemplates]);

  const updateTemplate = (eventKey, field, value) => {
    setTemplates(prev => ({
      ...prev,
      [eventKey]: {
        ...(prev[eventKey] || DEFAULTS[eventKey]),
        [field]: value,
      },
    }));
    setHasChanges(true);
  };

  const resetToDefaults = () => {
    if (!window.confirm('Reset all auto-message templates to defaults?')) return;
    setTemplates(JSON.parse(JSON.stringify(DEFAULTS)));
    setHasChanges(true);
    showToast('Reset to defaults. Click Save to persist.', 'info');
  };

  const save = async () => {
    try {
      setSaving(true);
      await updateConfig({ autoMessageTemplates: templates });
      setHasChanges(false);
      showToast('Auto-message templates saved', 'success');
    } catch (err) {
      console.error(err);
      showToast('Failed to save auto-message templates', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="auto-messages-editor" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ ...S.card, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ ...S.heading, margin: 0 }}>Auto-Message Templates</h2>
          <p style={{ fontSize: 12, color: C.textMid, margin: '4px 0 0' }}>
            System-generated messages sent to the employer portal when key events occur.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={resetToDefaults}
            style={{ ...S.buttonSecondary, fontSize: 12 }}
            data-testid="auto-msg-reset-btn"
          >
            <Icon name="refresh-outline" style={{ fontSize: 14, marginRight: 4 }} /> Reset to Defaults
          </button>
          <button
            onClick={save}
            disabled={!hasChanges || saving}
            style={{
              ...S.buttonPrimary,
              fontSize: 12,
              opacity: hasChanges && !saving ? 1 : 0.5,
              cursor: hasChanges && !saving ? 'pointer' : 'not-allowed',
            }}
            data-testid="auto-msg-save-btn"
          >
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>

      {EVENTS.map(ev => {
        const t = templates[ev.key] || DEFAULTS[ev.key];
        return (
          <div key={ev.key} style={{ ...S.card }} data-testid={`auto-msg-card-${ev.key}`}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12, gap: 12 }}>
              <div>
                <h3 style={{ ...S.heading, margin: 0 }}>{ev.label}</h3>
                <p style={{ fontSize: 12, color: C.textMid, margin: '4px 0 0' }}>{ev.description}</p>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: C.textMid }}>
                <input
                  type="checkbox"
                  checked={!!t.enabled}
                  onChange={(e) => updateTemplate(ev.key, 'enabled', e.target.checked)}
                  data-testid={`auto-msg-enabled-${ev.key}`}
                />
                {t.enabled ? 'Enabled' : 'Disabled'}
              </label>
            </div>

            <label style={{ ...S.label, display: 'block', marginBottom: 4 }}>Message Template</label>
            <textarea
              value={t.template || ''}
              onChange={(e) => updateTemplate(ev.key, 'template', e.target.value)}
              style={{ ...S.input, minHeight: 70, resize: 'vertical', fontFamily: 'inherit' }}
              data-testid={`auto-msg-template-${ev.key}`}
            />

            <div style={{ marginTop: 8, fontSize: 11, color: C.textLight }}>
              <strong style={{ color: C.textMid }}>Available variables:</strong>{' '}
              {ev.variables.map((v, i) => (
                <code key={v} style={{ background: C.bg, padding: '1px 6px', borderRadius: 4, marginLeft: i === 0 ? 0 : 4 }}>
                  {'{' + v + '}'}
                </code>
              ))}
            </div>

            <div style={{ marginTop: 10, padding: 10, background: C.bg, borderRadius: 8, fontSize: 12, color: C.textMid }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: C.textLight, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Preview</div>
              {renderPreview(t.template || '', ev.key)}
            </div>
          </div>
        );
      })}

      <HelpSection title="Auto-Messages Help" storageKey="auto-messages-help">
        <p><strong>How auto-messages work:</strong> When key events happen (contracts signed, banking verified, census uploaded, enrollment milestones), the system automatically posts a message to the employer's chat thread on their portal.</p>
        <p><strong>Variable substitution:</strong> Use <code>{'{variableName}'}</code> in the template to inject dynamic values. Each event has its own set of available variables shown below the template.</p>
        <p><strong>Disabling a template:</strong> Uncheck the Enabled toggle to prevent that auto-message from sending. Internal users can still send manual messages at any time.</p>
        <p><strong>Enrollment milestones:</strong> Fire at 5, 10, 25, 50, 75, and 100 enrollments, then every 25 after that (125, 150, …).</p>
      </HelpSection>
    </div>
  );
}

function renderPreview(template, eventKey) {
  const demo = {
    contract_signed: { contactName: 'Karen McIntyre', contractName: 'Master Services Agreement', companyName: 'Subway — Downtown Franchise' },
    banking_verified: { bankName: 'Chase', accountMasked: '••••4321' },
    census_uploaded: { count: '45', avgSalary: '$58,200' },
    enrollment_milestone: { count: '25' },
  }[eventKey] || {};

  let result = template;
  Object.entries(demo).forEach(([k, v]) => {
    result = result.replace(new RegExp('\\{' + k + '\\}', 'g'), v);
  });
  return result || <em style={{ color: C.textLight }}>Empty template</em>;
}
